import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
    getStorage,
    ref as storageRef,
    uploadBytes,
    getDownloadURL,
} from "firebase/storage";
import {
    getDatabase,
    ref as dbRef,
    set,
    push,
    remove, get,
} from "firebase/database";
import Select from "react-select";
import { DocumentEditModal } from "./DocumentEditModal";
import { TextDocumentEditor } from "./TextDocumentEditor";
import { Helmet } from "react-helmet-async";
import { BreadcrumbComponent, BreadcrumbItem } from "../../Components/BreadCrumb";
import { useBuilding } from "../../Helpers/BuildingContext";
import {getAuth} from "firebase/auth";
import COLORS from "../../Helpers/utils";
import { FileText, File, Upload, Edit3, Calendar, User, ChevronDown, ChevronRight } from "lucide-react";

interface BaseDocument {
    key: string;
    name: string;
    category?: string;
    uploadedAt?: string;
    uploadedBy?: string;
    lastModified?: string;
    lastModifiedBy?: string;
}

interface FileDocument extends BaseDocument {
    documentType: 'file';
    fileType: string;
    storageFilename: string;
    fileSize?: number;
}

interface TextDocument extends BaseDocument {
    documentType: 'text';
    textContent: string;
}

export type DocumentModel = FileDocument | TextDocument;

interface CategoryOption {
    value: string;
    label: string;
    allowsFiles?: boolean;
    allowsText?: boolean;
}

interface DocumentsPageProps {
    title: string;
    fileTypeOptions: CategoryOption[];
    allowTextDocuments?: boolean;
    allowFileUploads?: boolean;
}

const isFileDocument = (doc: DocumentModel): doc is FileDocument => {
    return doc.documentType === 'file';
};

const isTextDocument = (doc: DocumentModel): doc is TextDocument => {
    return doc.documentType === 'text';
};

const DocumentsPage: React.FC<DocumentsPageProps> = ({
                                                         title,
                                                         fileTypeOptions: customFileTypeOptions,
                                                         allowTextDocuments = true,
                                                         allowFileUploads = true
                                                     }) => {
    const [filteredDocuments, setFilteredDocuments] = useState<Record<string, DocumentModel>>({});
    const [groupedDocuments, setGroupedDocuments] = useState<Record<string, Array<{dbKey: string, doc: DocumentModel}>>>({});
    const [isDragging, setIsDragging] = useState(false);
    const [currentCategory, setCurrentCategory] = useState("all");
    const [error, setError] = useState<string | null>(null);

    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const [showFileModal, setShowFileModal] = useState(false);
    const [editDoc, setEditDoc] = useState<null | {
        dbKey: string;
        fileData: FileDocument;
    }>(null);

    const [showTextEditor, setShowTextEditor] = useState(false);
    const [editingTextDoc, setEditingTextDoc] = useState<{
        dbKey: string;
        document: TextDocument;
    } | null>(null);

    const [selectedPdf, setSelectedPdf] = useState<string | null>(null);
    const [selectedFileDoc, setSelectedFileDoc] = useState<string | null>(null);
    const [selectedTextDoc, setSelectedTextDoc] = useState<string | null>(null);
    const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

    const location = useLocation();
    const pathSegments = location.pathname.split("/");
    const buildingKey = pathSegments[pathSegments.length - 2];

    const { currentBuilding, loadingBuilding, buildingError, fetchBuilding } = useBuilding();

    const documents: Record<string, any> =
        currentBuilding?.model?.documents || currentBuilding?.model?.documents || {};

    interface UserCache {
        [uid: string]: string | null;
    }

    const [userCache, setUserCache] = useState<UserCache>({});

    const getUserName = async (uid: string | undefined): Promise<string> => {
        if (!uid) return 'Okänd användare';

        if (userCache[uid] !== undefined) {
            return userCache[uid] || 'Okänd användare';
        }

        try {
            const db = getDatabase();
            const userRef = dbRef(db, `users/userList/${uid}/name`);
            const snapshot = await get(userRef);
            const name = snapshot.val();

            setUserCache(prev => ({
                ...prev,
                [uid]: name
            }));

            return name || 'Okänd användare';
        } catch (error) {
            console.error('Error fetching user name:', error);
            setUserCache(prev => ({
                ...prev,
                [uid]: null
            }));
            return 'Okänd användare';
        }
    };

    const UserName: React.FC<{ uid: string | undefined }> = ({ uid }) => {
        const [userName, setUserName] = useState<string>('Laddar...');

        useEffect(() => {
            if (!uid) {
                setUserName('Okänd användare');
                return;
            }

            getUserName(uid).then(setUserName);
        }, [uid]);

        return <span>{userName}</span>;
    };

    // Helper function to get download URL from storage filename
    const getFileUrl = async (storageFilename: string): Promise<string> => {
        const storage = getStorage();
        const fileRef = storageRef(storage, `buildings/${buildingKey}/documents/${storageFilename}`);
        return await getDownloadURL(fileRef);
    };

    useEffect(() => {
        fetchBuilding(buildingKey).then();
    }, [buildingKey, fetchBuilding]);

    const categoryOptions: CategoryOption[] = [
        { value: "all", label: "Alla dokument" },
        ...customFileTypeOptions
    ];

    useEffect(() => {
        const convertedDocuments: Record<string, DocumentModel> = {};

        Object.entries(documents).forEach(([dbKey, doc]) => {
            if (!doc.documentType) {
                // Handle legacy documents that might have url but need storageFilename
                if (doc.fileType) {
                    convertedDocuments[dbKey] = {
                        ...doc,
                        documentType: 'file',
                        category: doc.fileType,
                        storageFilename: doc.storageFilename || doc.name // fallback to name if storageFilename missing
                    } as FileDocument;
                }
            } else {
                convertedDocuments[dbKey] = doc as DocumentModel;
            }
        });

        const filteredEntries = Object.entries(convertedDocuments).filter(([dbKey, doc]) => {
            if (currentCategory === "all") {
                const selectableCategories = customFileTypeOptions.map(opt => opt.value);
                return selectableCategories.includes(doc.category || '');
            }
            return doc.category === currentCategory;
        });

        const filtered = Object.fromEntries(filteredEntries) as Record<string, DocumentModel>;
        setFilteredDocuments(filtered);

        // Group documents by category
        const grouped: Record<string, Array<{dbKey: string, doc: DocumentModel}>> = {};

        if (currentCategory === "all") {
            // Group by individual categories
            filteredEntries.forEach(([dbKey, doc]) => {
                const category = doc.category || 'Okategoriserad';
                if (!grouped[category]) {
                    grouped[category] = [];
                }
                grouped[category].push({ dbKey, doc });
            });

            // Sort documents within each category
            Object.keys(grouped).forEach(category => {
                grouped[category].sort((a, b) =>
                    a.doc.name.toLowerCase().localeCompare(b.doc.name.toLowerCase())
                );
            });
        } else {
            // Single category view - no grouping needed
            grouped[currentCategory] = filteredEntries.map(([dbKey, doc]) => ({ dbKey, doc }))
                .sort((a, b) => a.doc.name.toLowerCase().localeCompare(b.doc.name.toLowerCase()));
        }

        setGroupedDocuments(grouped);

        // Initialize all sections as collapsed when showing "all" documents
        if (currentCategory === "all") {
            const newCollapsedSections: Record<string, boolean> = {};
            Object.keys(grouped).forEach(category => {
                newCollapsedSections[category] = true; // All sections collapsed by default
            });
            setCollapsedSections(newCollapsedSections);
        }
    }, [documents, currentCategory, customFileTypeOptions]);

    const selectableCategories = customFileTypeOptions.map(opt => opt.value);
    const relevantDocuments = Object.values(documents).filter(doc => {
        const category = doc.category || doc.fileType;
        return selectableCategories.includes(category || '');
    });

    const totalDocuments = relevantDocuments.length;
    const categoryCount = relevantDocuments.reduce(
        (acc: Record<string, number>, doc: any) => {
            const category = doc.category || doc.fileType || 'unknown';
            acc[category] = (acc[category] || 0) + 1;
            return acc;
        },
        {}
    );

    const updatedCategoryOptions = categoryOptions.map((option) => {
        if (option.value === "all") {
            return {
                ...option,
                label: `${option.label} (${totalDocuments})`,
            };
        }
        return {
            ...option,
            label: `${option.label} (${categoryCount[option.value] || 0})`,
        };
    });

    const selectableCategoryOptions = categoryOptions.filter(option => option.value !== "all");

    const handleDeleteDoc = async () => {
        if (!editDoc) return;

        const db = getDatabase();
        const { dbKey } = editDoc;
        try {
            const docRef = dbRef(db, `buildings/${buildingKey}/documents/${dbKey}`) ||
                dbRef(db, `buildings/${buildingKey}/documents/${dbKey}`);
            await remove(docRef);

            fetchBuilding(buildingKey).then();
        } catch (error) {
            console.error("Error deleting file:", error);
            setError("Kunde inte radera filen. Försök igen.");
        } finally {
            setEditDoc(null);
        }
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragging(false);

        const file = event.dataTransfer.files[0];
        if (!file) return;

        setPendingFile(file);
        setShowFileModal(true);
    };

    const generateUniqueFileName = (originalName: string): string => {
        const lastDotIndex = originalName.lastIndexOf('.');
        const nameWithoutExt = lastDotIndex !== -1 ? originalName.slice(0, lastDotIndex) : originalName;
        const extension = lastDotIndex !== -1 ? originalName.slice(lastDotIndex) : '';
        const randomString = Math.random().toString(36).substring(2, 7);
        return `${nameWithoutExt}_${randomString}${extension}`;
    };

    const handleConfirmName = async (customName: string, category: string) => {
        setError(null);
        if (!pendingFile) return;

        const storage = getStorage();
        const db = getDatabase();

        try {
            const documentsRef = dbRef(db, `buildings/${buildingKey}/documents`);

            setShowFileModal(false);
            const newDocRef = push(documentsRef);
            const uniqueFileName = generateUniqueFileName(pendingFile.name);

            const storageFileRef = storageRef(
                storage,
                `buildings/${buildingKey}/documents/${uniqueFileName}`
            );

            await uploadBytes(storageFileRef, pendingFile);

            if (!newDocRef.key) {
                throw new Error("Failed to generate document key");
            }

            const docData: FileDocument = {
                key: newDocRef.key,
                name: customName,
                documentType: 'file',
                category,
                fileType: pendingFile.name.split('.').pop() || '',
                storageFilename: uniqueFileName,
                fileSize: pendingFile.size,
                uploadedAt: new Date().toISOString(),
                lastModified: new Date().toISOString(),
            };

            await set(newDocRef, docData);

            fetchBuilding(buildingKey).then();
        } catch (error) {
            setError(
                "Något gick fel vid uppladdning. Kontrollera att det inte redan finns en fil med samma namn."
            );
            console.error("Error uploading file:", error);
        } finally {
            setPendingFile(null);
        }
    };

    const handleCancelName = () => {
        setShowFileModal(false);
        setPendingFile(null);
    };

    const handleEditFileConfirm = async (newName: string, newCategory: string) => {
        if (!editDoc) return;
        try {
            const db = getDatabase();
            const docRef = dbRef(
                db,
                `buildings/${buildingKey}/documents/${editDoc.dbKey}`
            );

            const updatedDoc: FileDocument = {
                ...editDoc.fileData,
                name: newName,
                category: newCategory,
                lastModified: new Date().toISOString(),
                lastModifiedBy: getAuth().currentUser?.uid
            };

            await set(docRef, updatedDoc);

            fetchBuilding(buildingKey).then();
        } catch (error) {
            console.error("Error updating file:", error);
            setError("Något gick fel vid uppdateringen.");
        } finally {
            setEditDoc(null);
        }
    };

    const handleCreateTextDoc = async (title: string, content: string, category: string) => {
        setError(null);
        const db = getDatabase();

        try {
            const documentsRef = dbRef(db, `buildings/${buildingKey}/documents`);
            const newDocRef = push(documentsRef);

            if (!newDocRef.key) {
                throw new Error("Failed to generate document key");
            }

            const textDoc: TextDocument = {
                key: newDocRef.key,
                name: title,
                documentType: 'text',
                category,
                textContent: content,
                uploadedAt: new Date().toISOString(),
                uploadedBy: getAuth().currentUser?.uid,
                lastModified: new Date().toISOString(),
                lastModifiedBy: getAuth().currentUser?.uid
            };

            await set(newDocRef, textDoc);
            fetchBuilding(buildingKey).then();
            setShowTextEditor(false);
        } catch (error) {
            setError("Något gick fel vid sparande av textdokumentet.");
            console.error("Error saving text document:", error);
        }
    };

    const handleEditTextDoc = async (title: string, content: string, category: string) => {
        if (!editingTextDoc) return;

        try {
            const db = getDatabase();
            const docRef = dbRef(db, `buildings/${buildingKey}/documents/${editingTextDoc.dbKey}`);

            const updatedDoc: TextDocument = {
                ...editingTextDoc.document,
                name: title,
                textContent: content,
                category,
                lastModified: new Date().toISOString(),
                lastModifiedBy: getAuth().currentUser?.uid,
            };

            await set(docRef, updatedDoc);
            fetchBuilding(buildingKey).then();
        } catch (error) {
            console.error("Error updating text document:", error);
            setError("Något gick fel vid uppdateringen.");
        } finally {
            setEditingTextDoc(null);
        }
    };

    const handleDeleteTextDoc = async () => {
        if (!editingTextDoc) return;

        const db = getDatabase();
        try {
            const docRef = dbRef(db, `buildings/${buildingKey}/documents/${editingTextDoc.dbKey}`);
            await remove(docRef);
            fetchBuilding(buildingKey).then();
        } catch (error) {
            console.error("Error deleting text document:", error);
            setError("Kunde inte radera dokumentet. Försök igen.");
        } finally {
            setEditingTextDoc(null);
            setSelectedTextDoc(null);
            setSelectedFileDoc(null);
        }
    };

    const handleDocumentClick = async (dbKey: string, doc: DocumentModel) => {
        if (isTextDocument(doc)) {
            setSelectedTextDoc(dbKey);
            setSelectedPdf(null);
            setSelectedFileDoc(null);
        } else if (isFileDocument(doc)) {
            try {
                const fileUrl = await getFileUrl(doc.storageFilename);
                setSelectedPdf(fileUrl);
                setSelectedFileDoc(dbKey);
                setSelectedTextDoc(null);
            } catch (error) {
                console.error("Error loading file:", error);
                setError("Kunde inte ladda filen.");
            }
        }
    };

    const handleEditClick = (dbKey: string, doc: DocumentModel) => {
        if (isTextDocument(doc)) {
            setEditingTextDoc({ dbKey, document: doc });
        } else if (isFileDocument(doc)) {
            setEditDoc({ dbKey, fileData: doc });
        }
    };

    const getCategoryDisplayName = (categoryValue: string): string => {
        const categoryOption = customFileTypeOptions.find(opt => opt.value === categoryValue);
        return categoryOption ? categoryOption.label : categoryValue;
    };

    const toggleSectionCollapse = (category: string) => {
        setCollapsedSections(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    const breadcrumbItems: BreadcrumbItem[] = [
        { text: currentBuilding?.summary.name ?? "Byggnad", link: `/building/${buildingKey}` },
        { text: title }
    ];

    if (loadingBuilding) {
        return <BreadcrumbComponent items={breadcrumbItems} />;
    }

    if (buildingError) {
        return <div style={{ color: "red" }}>{buildingError}</div>;
    }

    return (
        <div className="container-fluid h-100 d-flex flex-column">
            <Helmet>
                <title>{title}</title>
            </Helmet>
            {error && <div className="alert alert-danger" role="alert">{error}</div>}

            <BreadcrumbComponent items={breadcrumbItems} />

            {/* File Upload Modal */}
            <DocumentEditModal
                fileName={pendingFile?.name || ""}
                visible={showFileModal}
                onConfirm={handleConfirmName}
                onCancel={handleCancelName}
                fileTypeOptions={selectableCategoryOptions}
                isEditMode={false}
            />

            {/* File Edit Modal */}
            <DocumentEditModal
                fileName={editDoc?.fileData.name || ""}
                visible={!!editDoc}
                onConfirm={handleEditFileConfirm}
                onCancel={() => setEditDoc(null)}
                fileTypeOptions={selectableCategoryOptions}
                isEditMode={true}
                onDelete={handleDeleteDoc}
            />

            {/* Text Document Create Modal */}
            <TextDocumentEditor
                visible={showTextEditor}
                onSave={handleCreateTextDoc}
                onCancel={() => setShowTextEditor(false)}
                categoryOptions={selectableCategoryOptions}
                isEditMode={false}
            />

            {/* Text Document Edit Modal */}
            <TextDocumentEditor
                visible={!!editingTextDoc}
                onSave={handleEditTextDoc}
                onCancel={() => setEditingTextDoc(null)}
                onDelete={handleDeleteTextDoc}
                categoryOptions={selectableCategoryOptions}
                isEditMode={true}
                initialData={editingTextDoc ? {
                    title: editingTextDoc.document.name,
                    content: editingTextDoc.document.textContent,
                    category: editingTextDoc.document.category || ''
                } : undefined}
            />

            <div
                className="row flex-grow-1 mt-3"
                style={{
                    overflow: "hidden",
                    pointerEvents: showFileModal || editDoc || showTextEditor || editingTextDoc ? "none" : "auto",
                    filter: showFileModal || editDoc || showTextEditor || editingTextDoc ? "grayscale(50%) brightness(80%)" : "none",
                }}
            >
                <div
                    className={`col-12 col-md-4 col-lg-3 h-100 d-flex flex-column ${
                        isDragging ? "bg-info bg-opacity-10 border-2 border-info border-dashed" : "bg-light border"
                    }`}
                    style={{
                        maxWidth: "350px",
                        overflowY: "auto",
                    }}
                    onDragOver={(e) => {
                        e.preventDefault();
                        if (allowFileUploads) {
                            setIsDragging(true);
                        }
                    }}
                    onDrop={allowFileUploads ? handleDrop : undefined}
                    onDragLeave={() => setIsDragging(false)}
                >
                    <div className="p-3">
                        <h3 className="h5 mb-3">{title}</h3>
                        <div className="mb-3">
                            <Select
                                options={updatedCategoryOptions}
                                defaultValue={updatedCategoryOptions[0]}
                                value={updatedCategoryOptions.find((opt) => opt.value === currentCategory)}
                                onChange={(selectedOption) =>
                                    setCurrentCategory((selectedOption as any).value)
                                }
                                isDisabled={showFileModal || !!editDoc || showTextEditor || !!editingTextDoc}
                            />
                        </div>

                        {allowTextDocuments && (
                            <button
                                className="btn btn-primary w-100 mb-3"
                                onClick={() => setShowTextEditor(true)}
                                disabled={showFileModal || !!editDoc || showTextEditor || !!editingTextDoc}
                            >
                                <FileText className="me-2" size={16} />
                                Skapa textdokument
                            </button>
                        )}

                        {allowFileUploads && (
                            <div className="text-center text-muted fst-italic py-3">
                                <Upload className="mx-auto mb-2" size={32} />
                                Släpp filer här
                                <br />
                                för att ladda upp
                            </div>
                        )}
                    </div>

                    <div className="flex-grow-1 overflow-auto px-3 pb-3">
                        {Object.entries(groupedDocuments)
                            .sort(([categoryA], [categoryB]) => categoryA.localeCompare(categoryB))
                            .map(([category, documentsInCategory]) => (
                                <div key={category} className="mb-4">
                                    {currentCategory === "all" && (
                                        <div>
                                            <button
                                                className="btn btn-link text-start p-0 w-100 text-decoration-none"
                                                onClick={() => toggleSectionCollapse(category)}
                                                style={{ border: "none", background: "none" }}
                                            >
                                                <h5 className="text-uppercase fw-bold text-muted border-bottom pb-2 mb-3 small d-flex align-items-center justify-content-between">
                                                    <span>
                                                        {getCategoryDisplayName(category)} ({documentsInCategory.length})
                                                    </span>
                                                    {collapsedSections[category] ? (
                                                        <ChevronRight size={16} />
                                                    ) : (
                                                        <ChevronDown size={16} />
                                                    )}
                                                </h5>
                                            </button>
                                            
                                            {!collapsedSections[category] && (
                                                <div className="list-group list-group-flush">
                                                    {documentsInCategory.map(({ dbKey, doc }) => (
                                                        <div key={dbKey} className="d-flex align-items-center gap-2 mb-2">
                                                            <div
                                                                className={`list-group-item list-group-item-action d-flex align-items-center gap-2 flex-grow-1 ${
                                                                    (isTextDocument(doc) && selectedTextDoc === dbKey) ||
                                                                    (isFileDocument(doc) && selectedFileDoc === dbKey)
                                                                        ? "active" : ""
                                                                }`}
                                                                style={{
                                                                    cursor: "pointer",
                                                                    whiteSpace: "normal",
                                                                    overflowWrap: "break-word",
                                                                    wordBreak: "break-word",
                                                                }}
                                                                onClick={() => handleDocumentClick(dbKey, doc)}
                                                            >
                                                                {isTextDocument(doc) ? (
                                                                    <FileText className="text-primary" size={18} />
                                                                ) : (
                                                                    <File className="text-secondary" size={18} />
                                                                )}
                                                                <span className="flex-grow-1">{doc.name}</span>
                                                            </div>

                                                            <button
                                                                className="btn btn-outline-secondary btn-sm"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleEditClick(dbKey, doc);
                                                                }}
                                                            >
                                                                <Edit3 size={14} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {currentCategory !== "all" && (
                                        <div className="list-group list-group-flush">
                                            {documentsInCategory.map(({ dbKey, doc }) => (
                                                <div key={dbKey} className="d-flex align-items-center gap-2 mb-2">
                                                    <div
                                                        className={`list-group-item list-group-item-action d-flex align-items-center gap-2 flex-grow-1 ${
                                                            (isTextDocument(doc) && selectedTextDoc === dbKey) ||
                                                            (isFileDocument(doc) && selectedFileDoc === dbKey)
                                                                ? "active" : ""
                                                        }`}
                                                        style={{
                                                            cursor: "pointer",
                                                            whiteSpace: "normal",
                                                            overflowWrap: "break-word",
                                                            wordBreak: "break-word",
                                                        }}
                                                        onClick={() => handleDocumentClick(dbKey, doc)}
                                                    >
                                                        {isTextDocument(doc) ? (
                                                            <FileText className="text-primary" size={18} />
                                                        ) : (
                                                            <File className="text-secondary" size={18} />
                                                        )}
                                                        <span className="flex-grow-1">{doc.name}</span>
                                                    </div>

                                                    <button
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEditClick(dbKey, doc);
                                                        }}
                                                    >
                                                        <Edit3 size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                    </div>
                </div>

                <div className="col-12 col-md-8 col-lg-9 h-100 overflow-auto">
                    {selectedPdf ? (
                        <iframe
                            src={selectedPdf}
                            className="w-100 h-100 border-0"
                            title="PDF Viewer"
                        />
                    ) : selectedTextDoc ? (
                        <div className="card h-100">
                            <div className="card-body overflow-auto">
                                {(() => {
                                    const doc = filteredDocuments[selectedTextDoc];
                                    if (isTextDocument(doc)) {
                                        return (
                                            <>
                                                <div className="d-flex justify-content-between align-items-center mb-4">
                                                    <h3 className="card-title mb-0">{doc.name}</h3>
                                                    <div className="text-muted small">
                                                        <FileText className="me-1" size={14} />
                                                        Textdokument
                                                    </div>
                                                </div>
                                                
                                                <div className="alert alert-light border mb-4">
                                                    <div className="row">
                                                        <div className="col-md-6">
                                                            <small className="text-muted">
                                                                <Calendar className="me-1" size={12} />
                                                                <strong>Skapad:</strong> {doc.uploadedAt ? `${new Date(doc.uploadedAt).toLocaleDateString('sv-SE')} ${new Date(doc.uploadedAt).toLocaleTimeString('sv-SE')}` : 'Okänt'}
                                                                {doc.uploadedBy && (
                                                                    <> av <UserName uid={doc.uploadedBy} /></>
                                                                )}
                                                            </small>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <small className="text-muted">
                                                                <Calendar className="me-1" size={12} />
                                                                <strong>Uppdaterad:</strong> {doc.lastModified ? `${new Date(doc.lastModified).toLocaleDateString('sv-SE')} ${new Date(doc.lastModified).toLocaleTimeString('sv-SE')}` : 'Okänt'}
                                                                {doc.lastModifiedBy && (
                                                                    <> av <UserName uid={doc.lastModifiedBy} /></>
                                                                )}
                                                            </small>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="content-area" style={{
                                                    whiteSpace: "pre-wrap",
                                                    lineHeight: "1.6",
                                                    fontSize: "14px"
                                                }}>
                                                    {doc.textContent}
                                                </div>
                                            </>
                                        );
                                    }
                                    return null;
                                })()}
                            </div>
                        </div>
                    ) : (
                        <div className="d-flex align-items-center justify-content-center h-100">
                            <div className="text-center text-muted">
                                <FileText className="mx-auto mb-3" size={64} />
                                <h4>Välj en fil eller ett textdokument att förhandsvisa</h4>
                                <p className="mb-0">Klicka på ett dokument i listan till vänster för att visa innehållet här.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DocumentsPage;